import React, { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { CancelBtn } from 'components/Button/CancelBtn'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import {
    Animation,
    Button,
    ButtonsContainer,
    CursorPointer,
    Flex,
    ItemsGrid,
    TabFooter,
    ThinSeparator,
} from 'styling/GlobalStyles'
import { IEmployeesTab, INote, IDocumentState } from 'types/users.interface'
import { documentAccessRestrictionsOptions, documentCategoryOptions, noteCategoryOptions, noteVisibilityScopeOptions } from './constants'
import { ISelectValue } from 'constants/globalTypes'
import { BodyText, TitleText } from 'styling/Typography'
import { DropZone } from 'components/DropZone/DropZone'
import { formatDateForDisplay } from 'utils/helpers'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { EditIcon } from 'assets/icons/EditIcon'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { toast } from 'react-toastify'
import { DocumentIcon } from 'assets/icons/DocumentIcon'
import { Modal } from 'components/Modal/Modal'
import useModalClose from 'hooks/useModalClose'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import {
    TwoColumn,
    SidePanel,
    PanelScroll,
    EmptyState,
    ListRow,
    Snippet,
    DocListRow,
    DocumentIconSmall,
    IconAction,
} from './EmployeesNotesTab.styled'

type TabView = 'list' | 'notes' | 'documents'

function truncateNoteBody(content: string, max = 120) {
    if (!content?.trim()) return '—'
    if (content.length <= max) return content
    return `${content.slice(0, max)}…`
}


function normalizeCapability(
    capability: any,
): IDocumentState['capability'] | null {
    if (!capability) return null
    if (typeof capability === 'object' && capability.label && capability.value !== undefined) {
        return {
            value: capability.value,
            label: capability.label,
            requiresExpiry: Boolean(capability.requiresExpiry),
        }
    }
    if (typeof capability === 'object' && capability.name && capability.id !== undefined) {
        return {
            value: capability.id,
            label: capability.name,
            requiresExpiry: Boolean(capability.requiresExpiry),
        }
    }
    return null
}

export const EmployeesNotesTab = ({ state, setState, handleSubmit }: IEmployeesTab) => {
    const [view, setView] = useState<TabView>('list')
    const [previewNoteId, setPreviewNoteId] = useState<number | string | null>(null)
    const [previewDocumentId, setPreviewDocumentId] = useState<number | string | null>(null)
    const [editingNoteId, setEditingNoteId] = useState<number | string | null>(null)
    const [editingDocId, setEditingDocId] = useState<number | string | null>(null)
    const [docEditMode, setDocEditMode] = useState<'add' | 'edit'>('add')

    const previewNote = useMemo(
        () => state.notes.find((n) => (n.id ?? '') === previewNoteId) ?? null,
        [state.notes, previewNoteId],
    )

    const editingDoc = useMemo(
        () => (state.documents || []).find((d) => (d.id ?? '') === editingDocId) ?? null,
        [state.documents, editingDocId],
    )
    const previewDocument = useMemo(
        () => (state.documents || []).find((d) => (d.id ?? '') === previewDocumentId) ?? null,
        [state.documents, previewDocumentId],
    )
    const closeNotePreview = () => setPreviewNoteId(null)
    const closeDocumentPreview = () => setPreviewDocumentId(null)

    useModalClose(() => {
        closeNotePreview()
        closeDocumentPreview()
    })

    const addNote = () => {
        setState((prev) => ({
            ...prev,
            notes: [
                ...prev.notes,
                {
                    id: new Date().getTime(),
                    content: '',
                    visibilityScope: null,
                    category: null,
                } as INote,
            ],
        }))
        setEditingNoteId(null)
        setView('notes')
    }

    const handleNoteTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>, id: number | string) => {
        setState((prev) => ({
            ...prev,
            notes: prev.notes.map((note) =>
                (note.id ?? '') === id ? { ...note, content: e.target.value } : note,
            ),
        }))
    }

    const handleNoteSelect = (value: ISelectValue, name: string, id: number | string) => {
        setState((prev) => ({
            ...prev,
            notes: prev.notes.map((note) =>
                (note.id ?? '') === id ? { ...note, [name]: value.value } : note,
            ),
        }))
    }

    const removeNote = (id: number | string) => {
        setState((prev) => ({
            ...prev,
            notes: prev.notes.filter((n) => (n.id ?? '') !== id),
        }))
    }

    const handleDocumentSelect = (value: ISelectValue, name: string, id: number | string) => {
        setState((prev) => ({
            ...prev,
            documents: (prev.documents || []).map((doc) =>
                (doc.id ?? '') === id
                    ? { ...doc, [name]: name === 'capability' ? value : value.value }
                    : doc,
            ),
        }))
    }

    const handleDocumentDateChange = (date: Date | null, name: string | undefined, id: number | string) => {
        if (!name) return
        setState((prev) => ({
            ...prev,
            documents: (prev.documents || []).map((doc) =>
                (doc.id ?? '') === id ? { ...doc, [name]: date } : doc,
            ),
        }))
    }

    const handleDocumentFileChange = (file: File, id: number | string) => {
        setState((prev) => ({
            ...prev,
            documents: (prev.documents || []).map((doc) =>
                (doc.id ?? '') === id ? { ...doc, documentFile: file } : doc,
            ),
        }))
    }



    const addDocument = () => {
        const id = new Date().getTime()
        setState((prev) => ({
            ...prev,
            documents: [
                ...(prev.documents || []),
                {
                    id,
                    documentFile: null,
                    category: null,
                    accessRestrictions: null,
                    effectiveDate: null,
                    expiryDate: null,
                    capability: null,
                } as any,
            ],
        }))
        setEditingDocId(id)
        setPreviewDocumentId(null)
        setDocEditMode('add')
        setView('documents')
    }

    const openDocumentForEditing = (docId: number | string) => {
        setState((prev) => ({
            ...prev,
            documents: (prev.documents || []).map((doc) =>
                (doc.id ?? '') === docId
                    ? { ...doc, capability: normalizeCapability((doc as any).capability) ?? (doc as any).capability }
                    : doc,
            ),
        }))
        setEditingDocId(docId)
        setPreviewDocumentId(null)
        setDocEditMode('edit')
        setView('documents')
    }
    const openNoteForEditing = (noteId: number | string) => {
        setEditingNoteId(noteId)
        setPreviewNoteId(null)
        setView('notes')
    }

    const removeDocument = (id: number | string, e?: React.MouseEvent) => {
        e?.stopPropagation()
        setState((prev) => ({
            ...prev,
            documents: (prev.documents || []).filter((doc) => (doc.id ?? '') !== id),
        }))
    }

    const allDocuments = state.documents || []
    const documents = allDocuments.filter(
        (d) => d.alreadyCreated || d.capability || d.documentFile || d.category,
    )
    const hasAnyDocuments = documents.length > 0
    const docCategoryLabel = (doc: (typeof documents)[0]) =>
        documentCategoryOptions.find((o) => o.value == doc.category)?.label || ''

    const notePreviewModalJsx =
        previewNoteId !== null && previewNote ? (
            <Modal black>
                <Flex column gap="1rem" width="100%" style={{ minWidth: 'min(100%, 480px)' }}>
                    <Flex between width="100%" center>
                        <TitleText weight="bold">Note Preview</TitleText>
                        <CursorPointer onClick={closeNotePreview}>
                            <CloseIcon />
                        </CursorPointer>
                    </Flex>
                    <Flex column gap="0.35rem" style={{ marginTop: '0.25rem' }}>
                        <BodyText grey style={{ margin: 0, fontSize: 12 }}>
                            Note
                        </BodyText>
                        <BodyText style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{previewNote.content || '—'}</BodyText>
                    </Flex>
                    <ItemsGrid width="100%" columns={2} gap="1rem">
                        <Flex column gap="0.35rem">
                            <BodyText grey style={{ margin: 0, fontSize: 12 }}>
                                Visibility Scope
                            </BodyText>
                            <BodyText style={{ margin: 0 }}>
                                {noteVisibilityScopeOptions.find((o) => o.value == (previewNote as any).visibilityScope)
                                    ?.label || '—'}
                            </BodyText>
                        </Flex>
                        <Flex column gap="0.35rem">
                            <BodyText grey style={{ margin: 0, fontSize: 12 }}>
                                Category
                            </BodyText>
                            <BodyText style={{ margin: 0 }}>
                                {noteCategoryOptions.find((o) => o.value == previewNote.category)?.label || '—'}
                            </BodyText>
                        </Flex>
                    </ItemsGrid>
                </Flex>
            </Modal>
        ) : null

    const documentPreviewModalJsx =
        previewDocumentId !== null && previewDocument ? (
            <Modal black>
                <Flex column gap="1rem" width="100%" style={{ minWidth: 'min(100%, 520px)' }}>
                    <Flex between width="100%" center>
                        <TitleText weight="bold">Document Preview</TitleText>
                        <CursorPointer onClick={closeDocumentPreview}>
                            <CloseIcon />
                        </CursorPointer>
                    </Flex>
                    <Flex column gap="0.35rem">
                        <BodyText grey style={{ margin: 0, fontSize: 12 }}>
                            File
                        </BodyText>
                        {previewDocument.url ? (
                            <a href={previewDocument.url} target="_blank" rel="noopener noreferrer">
                                <BodyText style={{ textDecoration: 'underline', margin: 0 }}>Preview PDF</BodyText>
                            </a>
                        ) : (
                            <BodyText style={{ margin: 0 }}>—</BodyText>
                        )}
                    </Flex>
                    <ItemsGrid width="100%" columns={2} gap="1rem">
                        <Flex column gap="0.35rem">
                            <BodyText grey style={{ margin: 0, fontSize: 12 }}>
                                Capability
                            </BodyText>
                            <BodyText
                                style={{
                                    margin: 0,
                                    border: '1px solid #5d5d5d',
                                    borderRadius: 8,
                                    minHeight: 36,
                                    padding: '0.5rem 0.75rem',
                                }}
                            >
                                {(previewDocument as any)?.capability?.label ||
                                    (previewDocument as any)?.capability?.name ||
                                    '—'}
                            </BodyText>
                        </Flex>
                        <Flex column gap="0.35rem">
                            <BodyText grey style={{ margin: 0, fontSize: 12 }}>
                                Category
                            </BodyText>
                            <BodyText
                                style={{
                                    margin: 0,
                                    border: '1px solid #5d5d5d',
                                    borderRadius: 8,
                                    minHeight: 36,
                                    padding: '0.5rem 0.75rem',
                                }}
                            >
                                {documentCategoryOptions.find((o) => o.value == previewDocument.category)?.label || '—'}
                            </BodyText>
                        </Flex>
                        <Flex column gap="0.35rem">
                            <BodyText grey style={{ margin: 0, fontSize: 12 }}>
                                Access Restrictions
                            </BodyText>
                            <BodyText
                                style={{
                                    margin: 0,
                                    border: '1px solid #5d5d5d',
                                    borderRadius: 8,
                                    minHeight: 36,
                                    padding: '0.5rem 0.75rem',
                                }}
                            >
                                {documentAccessRestrictionsOptions.find(
                                    (o) => o.value == previewDocument.accessRestrictions,
                                )?.label || '—'}
                            </BodyText>
                        </Flex>
                        <Flex column gap="0.35rem">
                            <BodyText grey style={{ margin: 0, fontSize: 12 }}>
                                Effective Date
                            </BodyText>
                            <BodyText
                                style={{
                                    margin: 0,
                                    border: '1px solid #5d5d5d',
                                    borderRadius: 8,
                                    minHeight: 36,
                                    padding: '0.5rem 0.75rem',
                                }}
                            >
                                {previewDocument.effectiveDate
                                    ? formatDateForDisplay(previewDocument.effectiveDate as Date)
                                    : '—'}
                            </BodyText>
                        </Flex>
                        <Flex column gap="0.35rem">
                            <BodyText grey style={{ margin: 0, fontSize: 12 }}>
                                Expiration Date
                            </BodyText>
                            <BodyText
                                style={{
                                    margin: 0,
                                    border: '1px solid #5d5d5d',
                                    borderRadius: 8,
                                    minHeight: 36,
                                    padding: '0.5rem 0.75rem',
                                }}
                            >
                                {previewDocument.expiryDate
                                    ? formatDateForDisplay(previewDocument.expiryDate as Date)
                                    : '—'}
                            </BodyText>
                        </Flex>
                    </ItemsGrid>
                </Flex>
            </Modal>
        ) : null

    const renderListView = () => (
        <TwoColumn>
            <SidePanel>
                <TitleText weight="bold">Notes</TitleText>
                <PanelScroll>
                    {!state.notes.length && (
                        <EmptyState column center gap="1rem">
                            <DocumentIcon />
                            <BodyText grey>No Notes added</BodyText>
                        </EmptyState>
                    )}
                    {state.notes.map((note, index) => (
                        <ListRow
                            key={note.id ?? index}
                            onClick={() => setPreviewNoteId(note.id ?? index)}
                        >
                            <Snippet grey>{truncateNoteBody(note.content)}</Snippet>
                            <BodyText>{noteCategoryOptions.find((o) => o.value == note.category)?.label || '—'}</BodyText>
                            <BodyText>{noteVisibilityScopeOptions.find((o) => o.value == note.visibilityScope)?.label || '—'}</BodyText>
                            <Flex gap="0.35rem" alignStart onClick={(e) => e.stopPropagation()}>
                                <IconAction type="button" onClick={() => openNoteForEditing(note.id ?? index)}>
                                    <EditIcon />
                                </IconAction>
                                <IconAction type="button" onClick={() => removeNote(note.id ?? index)}>
                                    <CloseIcon />
                                </IconAction>
                            </Flex>
                        </ListRow>
                    ))}
                </PanelScroll>
                <Button orange type="button" onClick={addNote} width100>
                    {state.notes.length ? 'Add Another Note' : 'Add Note'}
                </Button>
            </SidePanel>

            <SidePanel>
                <TitleText weight="bold">Documents</TitleText>
                <PanelScroll>
                    {!hasAnyDocuments && (
                        <EmptyState column center gap="1rem">
                            <DocumentIcon />
                            <BodyText grey>No Documents added</BodyText>
                        </EmptyState>
                    )}
                    {documents.map((doc, index) => (
                        <DocListRow
                            key={doc.id ?? `d-${index}`}
                            between
                            width="100%"
                            onClick={() => setPreviewDocumentId(doc.id ?? index)}
                        >
                            <Flex gap="0.75rem" alignStart width="100%" style={{ minWidth: 0 }}>
                                <DocumentIconSmall>
                                    <DocumentIcon />
                                </DocumentIconSmall>
                                <Flex column gap="0.25rem" style={{ minWidth: 0 }}>
                                    <BodyText weight="bold" style={{ margin: 0 }}>
                                        {docCategoryLabel(doc) ||
                                            (doc as any)?.capability?.label ||
                                            (doc as any)?.capability?.name ||
                                            'Document'}
                                    </BodyText>
                                    <BodyText grey style={{ margin: 0, wordBreak: 'break-all' }}>
                                        {doc.fileName || doc.documentFile?.name || '/'}
                                    </BodyText>
                                </Flex>
                            </Flex>
                            <Flex gap="0.35rem" onClick={(e) => e.stopPropagation()}>
                                <IconAction type="button" onClick={() => openDocumentForEditing(doc.id ?? index)}>
                                    <EditIcon />
                                </IconAction>
                                <IconAction type="button" onClick={(e) => removeDocument(doc.id ?? index, e)}>
                                    <CloseIcon />
                                </IconAction>
                            </Flex>
                        </DocListRow>
                    ))}
                </PanelScroll>
                <Button type="button" orange onClick={addDocument} width100>
                    {documents.length ? 'Add Another' : 'Add'}
                </Button>
            </SidePanel>
        </TwoColumn>
    )

    const renderNotesView = () => (
        <Flex column gap="1rem" width="100%">
            <TitleText weight="bold">Notes</TitleText>
            {(editingNoteId !== null
                ? state.notes.filter((n) => (n.id ?? '') === editingNoteId)
                : state.notes.filter((n) => !n.alreadyCreated)
            ).map((note, index) => (
                <Flex key={note.id ?? index} column gap="0.75rem">
                    <Input
                        type="textarea"
                        placeholder="Notes"
                        labelText="Notes"
                        nomarg
                        noinvalidtext
                        value={note.content}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            handleNoteTextChange(e, note.id ?? index)
                        }
                        height="100px"
                        required
                    />
                    <ItemsGrid width="100%" columns={2} gap="1rem">
                        <HonestSelectInput
                            nomarg
                            noX
                            name="visibilityScope"
                            options={noteVisibilityScopeOptions}
                            labelText="Visibility Scope"
                            placeholder="Select"
                            onChange={(v, n) => handleNoteSelect(v as ISelectValue, n, note.id ?? index)}
                            defaultValue={(note as any)?.visibilityScope ?? undefined}
                            required
                        />
                        <HonestSelectInput
                            nomarg
                            noX
                            name="category"
                            options={noteCategoryOptions}
                            labelText="Category"
                            placeholder="Select"
                            onChange={(v, n) => handleNoteSelect(v as ISelectValue, n, note.id ?? index)}
                            defaultValue={(note as any)?.category ?? undefined}
                            required
                        />
                    </ItemsGrid>
                    {index <
                        (editingNoteId !== null
                            ? state.notes.filter((n) => (n.id ?? '') === editingNoteId).length
                            : state.notes.filter((n) => !n.alreadyCreated).length) -
                        1 && <ThinSeparator />}
                </Flex>
            ))}
            <Flex gap="1rem">
                <Button orange type="button" onClick={() => {
                    const drafts = state.notes.filter((n) => !n.alreadyCreated)
                    const hasEmpty = drafts.some((n) => !n.content?.trim())
                    if (hasEmpty) {
                        toast.warn('Please fill in all note fields before saving.')
                        return
                    }
                    setView('list')
                    setEditingNoteId(null)
                }}>
                    Save
                </Button>
                <Button
                    orange
                    type="button"
                    onClick={() => {
                        setState((prev) => ({
                            ...prev,
                            notes: [
                                ...prev.notes,
                                {
                                    id: new Date().getTime(),
                                    content: '',
                                    visibilityScope: null,
                                    category: null,
                                } as INote,
                            ],
                        }))
                    }}
                >
                    Add Another Note
                </Button>
                <Button type="button" onClick={() => {
                    setState((prev) => ({
                        ...prev,
                        notes: prev.notes.filter((n) => n.alreadyCreated || n.content?.trim()),
                    }))
                    setView('list')
                    setEditingNoteId(null)
                }}>
                    Cancel
                </Button>
            </Flex>
        </Flex>
    )

    const renderDocumentsView = () => {


        const doc = editingDoc
        console.log(editingDoc, 'editing doc')
        if (!doc) return null
        const docId = doc.id ?? editingDocId!
        const capabilityDefaultLabel =
            normalizeCapability((doc as any).capability)?.label ||
            (doc as any)?.capability?.label ||
            (doc as any)?.capability?.name ||
            (doc as any)?.type ||
            ''
        return (
            <Flex column gap="1rem" width="100%">
                <TitleText weight="bold">{doc.alreadyCreated ? 'Edit Document' : 'Add Document'}</TitleText>

                <BodyText grey>Add file</BodyText>
                <DropZone
                    acceptAll
                    file={doc.documentFile}
                    setFile={(file: File) => handleDocumentFileChange(file, docId)}
                />

                <ItemsGrid width="100%" columns={2}>
                    <HonestSelectAsyncInput
                        keyProp={Number(docId) || 0}
                        link="capability"
                        placeholder="Search capability"
                        name="capability"
                        labelText="Capability"
                        onChange={(v, n) => handleDocumentSelect(v as ISelectValue, n, docId)}
                        parserFunction={(data) =>
                            data.data.map((dIn: any) => ({
                                requiresExpiry: dIn.requiresExpiry,
                                label: dIn.name,
                                value: dIn.id,
                            }))
                        }
                        defaultValue={capabilityDefaultLabel}
                        nomarg
                        required
                    />
                    <HonestSelectInput
                        nomarg
                        noX
                        name="category"
                        options={documentCategoryOptions}
                        labelText="Category"
                        placeholder="Select..."
                        onChange={(v, n) => handleDocumentSelect(v as ISelectValue, n, docId)}
                        defaultValue={(doc as any)?.category ?? undefined}
                        required
                    />
                    <HonestSelectInput
                        nomarg
                        noX
                        name="accessRestrictions"
                        options={documentAccessRestrictionsOptions}
                        labelText="Access Restrictions"
                        placeholder="Select..."
                        onChange={(v, n) => handleDocumentSelect(v as ISelectValue, n, docId)}
                        defaultValue={(doc as any)?.accessRestrictions ?? undefined}
                    />
                </ItemsGrid>
                <ItemsGrid width="100%" columns={2}>
                    <CalendarInput
                        nomarg
                        width="100%"
                        name="effectiveDate"
                        labelText="Effective Date"
                        minDate={new Date()}
                        onChange={(date, name) =>
                            handleDocumentDateChange(date, name, docId)
                        }
                        defaultValue={doc?.effectiveDate || null}
                        required
                    />
                    <CalendarInput
                        nomarg
                        width="100%"
                        name="expiryDate"
                        minDate={doc.effectiveDate}
                        labelText="Expiration Date"
                        onChange={(date, name) =>
                            handleDocumentDateChange(date, name, docId)
                        }
                        defaultValue={doc?.expiryDate || null}
                        required
                    />
                </ItemsGrid>
                <Flex gap="1rem">
                    <Button orange type="button" onClick={() => {
                        if (!doc.documentFile && !doc.url) {
                            toast.warn('Please upload a document file before saving.')
                            return
                        }
                        if (!doc.capability) {
                            toast.warn('Please select a capability before saving.')
                            return
                        }
                        const cap = doc.capability as IDocumentState['capability'] | null
                        if (cap?.requiresExpiry && (!doc.effectiveDate || !doc.expiryDate)) {
                            toast.warn('Please fill in effective and expiration dates for this capability.')
                            return
                        }
                        setEditingDocId(null)
                        setView('list')
                    }}>
                        Save
                    </Button>
                    {docEditMode === 'add' && (
                        <Button
                            orange
                            type="button"
                            onClick={() => {
                                if (!doc.documentFile && !doc.url) {
                                    toast.warn('Please upload a document file before adding another.')
                                    return
                                }
                                if (!doc.capability) {
                                    toast.warn('Please select a capability before adding another.')
                                    return
                                }
                                const cap = doc.capability as IDocumentState['capability'] | null
                                if (cap?.requiresExpiry && (!doc.effectiveDate || !doc.expiryDate)) {
                                    toast.warn('Please fill in effective and expiration dates before adding another.')
                                    return
                                }
                                const newId = new Date().getTime()
                                setState((prev) => ({
                                    ...prev,
                                    documents: [
                                        ...(prev.documents || []),
                                        {
                                            id: newId,
                                            documentFile: null,
                                            category: null,
                                            accessRestrictions: null,
                                            effectiveDate: null,
                                            expiryDate: null,
                                            capability: null,
                                        } as any,
                                    ],
                                }))
                                setEditingDocId(newId)
                            }}
                        >
                            Add Another Document
                        </Button>
                    )}
                    <Button type="button" onClick={() => {
                        if (doc && !doc.alreadyCreated) {
                            setState((prev) => ({
                                ...prev,
                                documents: (prev.documents || []).filter((d) => (d.id ?? '') !== docId),
                            }))
                        }
                        setEditingDocId(null)
                        setView('list')
                    }}>
                        Cancel
                    </Button>
                </Flex>
            </Flex>
        )
    }

    const renderContent = () => {
        switch (view) {
            case 'notes':
                return renderNotesView()
            case 'documents':
                return renderDocumentsView()
            default:
                return renderListView()
        }
    }

    return (
        <>
            <Animation>
                {renderContent()}

                <TabFooter>
                    <ButtonsContainer>
                        <CancelBtn />
                        <Button orange onClick={handleSubmit}>
                            Save
                        </Button>
                    </ButtonsContainer>
                </TabFooter>
            </Animation>

            {notePreviewModalJsx && createPortal(notePreviewModalJsx, document.body)}
            {documentPreviewModalJsx && createPortal(documentPreviewModalJsx, document.body)}
        </>
    )
}
